<template>
  <div v-if="mode == 'top' || mode == 'pla'" class="product-head" :class="[mode]">
    <div class="info-block">
      <div class="product-name">
        <p class="title">{{ collectionTitle }}</p>
      </div>
      <div v-if="!isProductDiscontinued" class="sale-price" :class="{ show: showHeadCart }">
        <Price mode="top" />
        <AddToCartButton :products="productToCartFormat" class="add-to-cart-button top-cart-button" @added="trackCartProducts">
          <span class="af af-plus"></span>&nbsp;<span class="af af-cart"></span>
        </AddToCartButton>
      </div>
    </div>
    <div
      v-if="showReviewsInfo && product && product.review && product.review.rating && product.review.count >= 0"
      class="reviews-info"
      :class="[{ 'hide-top-info': hideTopInfo }]"
      @click="redirectToProductPageReviews"
    >
      <div class="reviews-count-star">
        <span class="af-star-outlined" />
      </div>
      <span v-if="product.review.rating">{{ product.review.rating }} Stars | {{ product.review.count }} Reviews</span>
      <span v-else>No reviews yet</span>
    </div>
    <PriceExtraInfo v-if="!isProductDiscontinued" :hide-top-info="hideTopInfo" mode="top" />
  </div>

  <div v-else-if="mode == 'desktop'" class="product-head" :class="[mode]">
    <CartAnimationPlaceholder />
    <div class="product-primary-information">
      <div class="product-name">
        <h1 class="title">{{ collectionTitle }}</h1>
      </div>
      <template v-if="!isProductDiscontinued && price.price">
        <div class="sale-price" :class="{ show: showHeadCart }">
          <AddToCartButton :products="productToCartFormat" :animate="true" class="add-to-cart-button" @added="trackCartProducts" />
          <Price mode="desktop" />
        </div>
      </template>
    </div>

    <template v-if="!isProductDiscontinued && price.price">
      <div class="extra-info-container">
        <PriceExtraInfo v-if="isProductAvailable" mode="desktop" />
      </div>
    </template>
    <div v-else class="sale-price">
      <a class="button no-margin uppercase" :href="categoryUrl">Shop All {{ categoryName }}</a>
    </div>
  </div>

  <div v-else-if="mode == 'aside'" class="product-head" :class="[mode]">
    <CartAnimationPlaceholder />
    <div class="product-primary-information">
      <div class="product-name">
        <h1 class="title">{{ collectionTitle }}</h1>
      </div>
      <template v-if="!isProductDiscontinued && price.price">
        <div class="sale-price" :class="{ show: showHeadCart }">
          <Price mode="desktop" />
        </div>
      </template>
    </div>

    <AddToCartButton :products="productToCartFormat" :animate="true" class="add-to-cart-button full-width-button" @added="trackCartProducts" />

    <div class="clear" />

    <DeliveryInfo mode="aside" v-if="mounted" />

    <div class="clear" />

    <template v-if="!isProductDiscontinued && price.price">
      <div v-if="isProductAvailable" class="extra-info-container">
        <PriceExtraInfo mode="aside" />
      </div>
    </template>

    <div v-else class="sale-price">
      <a class="button no-margin uppercase" :href="categoryUrl">Shop All {{ categoryName }}</a>
    </div>
  </div>

  <div v-else-if="mode == 'mobile'" class="product-head" :class="[mode]">
    <template v-if="!isProductDiscontinued" && price.price>
      <div class="sale-price" :class="{ show: showHeadCart }">
        <AddToCartButton :products="productToCartFormat" :show-loader="true" class="add-to-cart-button" @added="trackCartProducts" />
      </div>
    </template>
    <a v-else class="button uppercase" :href="categoryUrl">Shop All {{ categoryName }}</a>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapState, mapMutations
} from 'vuex';
import { MediaUtils } from '@article/pixel';
import AddToCartButton from '@/components/commons/AddToCartButton.vue';
import DeliveryInfo from './DeliveryInfo.vue';
import CartAnimationPlaceholder from './CartAnimationPlaceholder.vue';
import PriceExtraInfo from './PriceExtraInfo.vue';
import Price from './Price.vue';

export default {
  name: 'ProductHead',
  components: {
    DeliveryInfo,
    CartAnimationPlaceholder,
    PriceExtraInfo,
    Price,
    AddToCartButton,
  },
  props: {
    mode: {
      required: true,
      validator(value) {
        return ['top', 'desktop', 'mobile', 'pla', 'aside'].includes(value);
      },
    },
    showReviewsInfo: {
      default: false,
    },
  },
  computed: {
    ...mapState('product', [
      'product', //
      'showHeadCart',
      'hideTopInfo',
      'ugcData',
    ]),
    ...mapGetters('product', [
      'productToCartFormat', //
      'currentNavigationCategory',
      'price',
    ]),

    collectionTitle() {
      if (this.product && this.product.collection) {
        return this.product.collection.name;
      }
      return '';
    },

    categoryName() {
      if (this.currentNavigationCategory) {
        return this.currentNavigationCategory.displayName;
      }
      return '';
    },

    categoryUrl() {
      const category = this.currentNavigationCategory;
      if (category) {
        return this.$router.resolve({ name: 'browse', params: { id: category.id, slug: `${category.parentSlug}-${category.slug}` } }).href;
      }
      return this.$router.resolve({ name: 'home' }).href;
    },

    isProductDiscontinued() {
      return this.product && this.product.status === 'DISCONTINUED';
    },

    isProductAvailable() {
      return this.product && this.product.status === 'AVAILABLE';
    },
  },
  data() {
    return {
      mounted: false
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onWindowScroll);
    this.mounted = true;
  },
  destroyed() {
    window.removeEventListener('scroll', this.onWindowScroll);
  },
  methods: {
    ...mapMutations('product', [
      'updateHeadCartVisibility', //
      'updateTopInfo',
    ]),
    ...mapActions('product', [
      'trackCartProducts', //
    ]),
    onWindowScroll() {
      const showHeadCart = MediaUtils.isTabletAndSmaller && window.scrollY >= 450;
      const hideTopInfo = MediaUtils.isTabletAndSmaller && window.scrollY >= 60;
      if (showHeadCart !== this.showHeadCart) {
        this.updateHeadCartVisibility(MediaUtils.isTabletAndSmaller && window.scrollY >= 450);
      }
      if (hideTopInfo !== this.hideTopInfo) {
        this.updateTopInfo(MediaUtils.isTabletAndSmaller && window.scrollY >= 60);
      }
    },
    redirectToProductPageReviews() {
      window.location.href = `${this.product.productUrl}?pr=true`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@article/pixel/dist/style/variables';

.product-head {
  &.top,
  &.pla {
    position: sticky;
    top: 0;
    left: 0;
    overflow: hidden;
    z-index: 11;

    .info-block {
      display: flex;
      justify-content: space-between;
      align-items: center;
      z-index: 49;
      height: 60px;
      overflow: hidden;
      background: #ffffff;

      .product-name {
        margin-left: 20px;

        .title {
          color: var(--font-color);
          font-size: 1.3em;
          text-transform: uppercase;
          margin: 0px;
        }
      }

      .sale-price {
        display: flex;
        align-items: center;
        position: absolute;
        right: -76px;
        transform: translateZ(0);
        transition: right ease-in 250ms;

        &.show {
          right: 20px;
          transition: right ease-out 250ms;
        }
      }
    }

    .reviews-info {
      display: inline-flex;
      margin-top: 8px;
      line-height: 1em;
      white-space: nowrap;
      transition: transform ease-in 450ms, opacity ease-in 450ms;
      cursor: pointer;
      user-select: none;
      -webkit-tap-highlight-color: transparent;

      @media (max-width: $tablet-breakpoint - 1) {
        margin-top: -8px;
        margin-left: 20px;
      }

      .reviews-count-star {
        margin-right: 11px;
      }

      span:not(.af-star-outlined) {
        margin-top: 2px;
        font-size: var(--font-size-14);
      }

      &.hide-top-info {
        transform: translateY(-20px);
        opacity: 0;
        transition-timing-function: ease-out;
      }

      &:hover {
        color: var(--grey-light);

        img {
          opacity: 0.5;
        }
      }
    }
  }

  &.top {
    @media (min-width: $tablet-breakpoint) {
      display: none;
    }
  }

  &.desktop {
    display: block;
    color: var(--grey-dark);
    z-index: 9;
    position: absolute;
    bottom: 4px;
    right: 0;
    top: -4px;
    padding: 0px;

    .product-primary-information {
      display: flex;
      justify-content: flex-end;
    }

    .product-name {
      font-size: 1.5em;
      text-transform: uppercase;
      h1 {
        margin: 0px;
        color: var(--font-color);
      }
      .title {
        font-size: 1em;
        font-family: var(--font-regular);
        line-height: 40px;
        color: var(--font-color);
      }
    }
    .sale-price {
      margin-left: 20px;
      color: var(--accent-color);

      .submitBtn {
        height: 38px;
        font-size: var(--font-size-14);
        border: none;
        background-color: var(--accent-color);
        color: var(--white);
        border-radius: 20px;
        padding: 0px 10px 0px 10px;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin: 0px;
        &:hover {
          background-color: #ff5244;
        }
        -webkit-appearance: none;
      }
    }
    .free-shipping-container {
      margin-right: 12px;
    }

    @media (max-width: $tablet-breakpoint - 1) {
      display: none;
    }
  }

  &.aside {
    .jump-to-reviews {
      font-size: var(--font-size-14);
      cursor: pointer;
      &:hover {
        span {
          color: var(--coral);
        }
      }
    }
    .product-name {
      width: 49%;
      display: inline-block;
    }

    .prices.desktop {
      margin: 0;
    }

    .sale-price {
      width: 49%;
      display: inline-block;
      text-align: right;
    }

    .extra-info-container {
      width: 100%;
      .extra-info.desktop {
        text-align: left;
      }
    }
    .af {
      margin-right: 12px;
      color: var(--coral);
    }
  }

  &.mobile {
    text-align: center;
    margin-bottom: 12px;

    @media (min-width: $tablet-breakpoint - 1) {
      display: none;
    }
  }

  .affirm-message,
  .paybright-message {
    color: var(--grey-medium);
    font-size: var(--font-size-12);

    a {
      text-decoration: underline;
    }
  }

  .add-to-cart-button {
    @media (min-width: $tablet-breakpoint) {
      float: right;
    }

    &::v-deep {
      button,
      .button {
        width: 160px;
      }
    }

    &.full-width-button {
      float: none;

      &::v-deep button {
        width: 100%;
      }
    }

    &.top-cart-button {
      &::v-deep button {
        display: flex;
        width: 100%;
        padding: 8px 13px;
        font-size: 1.2em;
      }
    }
  }

  &.pla {
    .info-block {
      .product-name {
        margin-left: 20px;
        letter-spacing: 1.4px;
        font-size: 1.5em;

        .title {
          font-size: inherit;
        }
      }

      .regularPrice {
        font-size: 1.5em;
      }

      @media (max-width: $tablet-breakpoint) {
        .product-name {
          letter-spacing: unset;
          font-size: 1.3em;
        }
      }
    }

    @media (min-width: $tablet-breakpoint) {
      position: relative;

      .info-block {
        align-items: baseline;
        height: auto;

        .product-name {
          margin-left: 0px;
        }

        .sale-price {
          position: relative;

          .prices {
            margin-right: 75px;
          }

          .add-to-cart-container {
            display: none;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';

.product-head {
  &.pla {
    .price-extra-info-container {
      justify-content: flex-start;

      .extra-info {
        margin-top: 0px;
        margin-bottom: 8px;
        margin-left: 1px;
        text-align: left;

        @media (max-width: $tablet-breakpoint) {
          margin-left: 20px;
        }

        .extra-info-text:hover {
          color: var(--grey-light);
        }

        .af-about-small {
          margin-right: 12px;
        }

        .affirm-message {
          display: flex;

          .affirm-modal-trigger {
            margin-left: var(--space-2);
          }
        }
      }
    }
  }
}
</style>
